import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CustomValidator } from '../../../../services/custom-validator/custom-validator.service';
import { ToastrService } from 'ngx-toastr';
import { FaceCompareService } from '../../../../services/face-compare/face-compare.service';
import { SearchServiceStatusService } from '../../../../services/search-service-status/search-service-status.service';
import { SearchHistoryService } from '../../../../services/search-history/search-history.service';
import { UtilsService } from '../../../../services/utils/utils.service';
import { PaginatorComponent } from '../../../common/paginator/paginator.component';

@Component({
  selector: 'app-face-compare-uploader',
  imports: [CommonModule, PaginatorComponent],
  templateUrl: './face-compare-uploader.component.html',
  styleUrl: './face-compare-uploader.component.css',
})
export class FaceCompareUploaderComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  uploadedImages: any[] = [];
  maxImages = 4;
  maxSelectedImages = 2;
  showHistoryPanel = false;
  detectCreditCost: number | null = null;
  compareCreditCost: number | null = null;

  loadingCompareHistory = false;
  compareHistoryRows: any[] = [];
  compareHistoryCount = 0;
  compareHistoryPerPage = 5;
  compareHistoryCurrentPage = 1;

  private uploadsSub?: Subscription;
  private historySub?: Subscription;
  private creditsSub?: Subscription;

  get compareHistoryPagedStartAndEnd() {
    if (!this.compareHistoryRows?.length || !this.compareHistoryCount) {
      return { start: 0, end: 0 };
    }
    const start =
      (this.compareHistoryCurrentPage - 1) * this.compareHistoryPerPage + 1;
    const end = Math.min(
      start + this.compareHistoryRows.length - 1,
      this.compareHistoryCount,
    );
    return { start, end };
  }

  constructor(
    private toastr: ToastrService,
    private faceCompareService: FaceCompareService,
    private router: Router,
    private searchServiceStatusService: SearchServiceStatusService,
    private searchHistoryService: SearchHistoryService,
    private utilsService: UtilsService,
  ) {}

  ngOnInit(): void {
    this.uploadsSub = this.faceCompareService.uploadedImages$.subscribe(
      (images) => {
        this.uploadedImages = images;
      },
    );

    if (
      this.faceCompareService.consumeOpenHistoryRequest() &&
      this.uploadedImages.length > 0
    ) {
      this.showHistoryPanel = true;
    }

    this.historySub = this.faceCompareService.viewUploadsHistory$.subscribe(
      () => {
        this.faceCompareService.consumeOpenHistoryRequest();
        if (this.uploadedImages.length > 0) {
          this.showHistoryPanel = true;
        }
      },
    );

    this.creditsSub = this.searchServiceStatusService.creditCosts$.subscribe(
      (cc) => {
        this.detectCreditCost =
          this.searchServiceStatusService.resolveCreditCost(
            cc,
            'faceDetectCreditCost',
          );
        this.compareCreditCost =
          this.searchServiceStatusService.resolveCreditCost(
            cc,
            'faceCompareCreditCost',
          );
      },
    );

    this.loadCompareHistory();
  }

  ngOnDestroy(): void {
    this.uploadsSub?.unsubscribe();
    this.historySub?.unsubscribe();
    this.creditsSub?.unsubscribe();
  }

  get selectedImagesCount(): number {
    return this.uploadedImages.filter((img) => img.selected).length;
  }

  get compareSlots(): (any | null)[] {
    const selected = this.uploadedImages.filter((img) => img.selected);
    return [selected[0] ?? null, selected[1] ?? null];
  }

  get hasImages(): boolean {
    return this.uploadedImages.length > 0;
  }

  triggerFileInput(): void {
    if (this.uploadedImages.length >= this.maxImages) {
      this.toastr.error(`Maximum ${this.maxImages} images allowed`);
      return;
    }
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
      input.value = '';
    }
  }

  onFrameClick(slotIndex: number): void {
    const slot = this.compareSlots[slotIndex];
    if (!slot) {
      this.triggerFileInput();
    }
  }

  toggleImageSelection(index: number): void {
    const image = this.uploadedImages[index];
    if (!image) {
      return;
    }

    if (
      !image.selected &&
      this.selectedImagesCount >= this.maxSelectedImages
    ) {
      this.toastr.error(
        `Please select only ${this.maxSelectedImages} images`,
      );
      return;
    }

    image.selected = !image.selected;
    this.faceCompareService.updateUploadedImages([...this.uploadedImages]);
  }

  removeImage(index: number): void {
    this.uploadedImages.splice(index, 1);
    this.faceCompareService.updateUploadedImages([...this.uploadedImages]);
    if (this.uploadedImages.length === 0) {
      this.showHistoryPanel = false;
    }
  }

  removeSelectedSlot(slotIndex: number): void {
    const slot = this.compareSlots[slotIndex];
    if (!slot) {
      return;
    }
    const index = this.uploadedImages.findIndex((img) => img === slot);
    if (index >= 0) {
      this.uploadedImages[index].selected = false;
      this.faceCompareService.updateUploadedImages([...this.uploadedImages]);
    }
  }

  async mirrorImage(index: number): Promise<void> {
    if (!this.uploadedImages[index]) {
      return;
    }
    try {
      const mirroredUrl = await this.utilsService.createMirroredImage(
        this.uploadedImages[index].url,
      );
      this.uploadedImages[index].url = mirroredUrl;
      this.faceCompareService.updateUploadedImages([...this.uploadedImages]);
    } catch {
      this.toastr.error('Failed to mirror image');
    }
  }

  closeHistoryPanel(): void {
    this.showHistoryPanel = false;
  }

  detectFaces(): void {
    if (this.selectedImagesCount !== this.maxSelectedImages) {
      this.toastr.error(
        `Please select any ${this.maxSelectedImages} images`,
      );
      return;
    }

    const selectedImages = this.uploadedImages.filter((img) => img.selected);
    this.faceCompareService.setSelectedImages(selectedImages);
    this.router.navigate(['/osint-image-search/face-compare/detector']);
  }

  loadCompareHistory(): void {
    this.loadingCompareHistory = true;
    this.searchHistoryService
      .getFaceCompareHistories(
        this.compareHistoryCurrentPage,
        this.compareHistoryPerPage,
      )
      .subscribe({
        next: (response) => {
          this.compareHistoryRows = response?.data?.data_list || [];
          this.compareHistoryCount = response?.data?.total_items || 0;
          this.loadingCompareHistory = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingCompareHistory = false;
          this.toastr.error(
            error?.error?.message_key ||
              error?.error?.message ||
              'Failed to load compare history',
            'Load History Failed',
          );
        },
      });
  }

  onCompareHistoryPageChange(page: number): void {
    this.compareHistoryCurrentPage = page;
    this.loadCompareHistory();
  }

  openCompareHistoryResult(row: any): void {
    if (!row?.face_compare_id) {
      return;
    }

    this.router.navigate(['/osint-image-search/face-compare/detector'], {
      queryParams: { face_compare_id: row.face_compare_id },
      state: {
        isStaleResult: true,
        faceCompareId: row.face_compare_id,
        faceCompareHistory: row,
      },
    });
  }

  formatMatch(match: any): string {
    if (match === true) return 'Match';
    if (match === false) return 'No match';
    return '—';
  }

  formatProbability(probability: any): string {
    if (probability === null || probability === undefined || probability === '') {
      return '—';
    }
    const value = Number(probability);
    if (!Number.isFinite(value)) {
      return '—';
    }
    const percent = value <= 1 ? value * 100 : value;
    return `${percent.toFixed(1)}%`;
  }

  historyNotesLabel(row: any): string {
    const notes = String(row?.notes || '').trim();
    if (!notes) {
      return 'Face Compare';
    }
    return notes;
  }

  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') {
      return '';
    }
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) {
      return text;
    }
    return words.slice(0, maxWords).join(' ') + ' .....';
  }

  onHistoryImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = 'https://placehold.co/200x200?text=Image';
    }
  }

  private async handleFiles(files: File[]): Promise<void> {
    const remainingSlots = this.maxImages - this.uploadedImages.length;
    const filesToProcess = files.slice(0, remainingSlots);

    if (filesToProcess.length === 0) {
      this.toastr.error(`Maximum ${this.maxImages} images allowed`);
      return;
    }

    for (const file of filesToProcess) {
      try {
        const validationResult = await CustomValidator.validateImageFile(file);
        if (validationResult) {
          const errorMessage = Object.values(validationResult)[0] as string;
          this.toastr.error(errorMessage, 'File Validation Error');
          continue;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const shouldSelect =
            this.uploadedImages.filter((img) => img.selected).length <
            this.maxSelectedImages;
          this.uploadedImages.push({
            id: Date.now() + Math.random(),
            name: file.name,
            url: e.target?.result as string,
            file,
            selected: shouldSelect,
          });
          this.faceCompareService.updateUploadedImages([
            ...this.uploadedImages,
          ]);
        };
        reader.readAsDataURL(file);
      } catch {
        this.toastr.error(`Error processing ${file.name}`);
      }
    }

    if (files.length > remainingSlots) {
      this.toastr.error(`Maximum ${this.maxImages} images allowed`);
    }
  }
}
